import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AssignedUserRemoval = ({ assetIds, funcion, selected, onChange, disabled }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError('');
    axios.post(`${API}/admin/assets/assigned-users`, { asset_ids: assetIds, funcion },
      { withCredentials: true, signal: controller.signal })
      .then(r => { if (!controller.signal.aborted) setData(r.data); })
      .catch(e => {
        if (!controller.signal.aborted) setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'No se pudieron cargar las personas asignadas.');
      });
    return () => controller.abort();
  }, [assetIds, funcion, retry]);

  if (error) return (
    <div className="p-4 space-y-3" role="alert" data-testid="bulk-reassign-assigned-error">
      <p className="text-sm text-red-700">{error}</p>
      <button type="button" onClick={() => setRetry(n => n + 1)} disabled={disabled}
        className="btn-secondary text-sm flex gap-2 items-center" data-testid="bulk-reassign-assigned-retry">
        <RefreshCw className="w-4 h-4" />Reintentar
      </button>
    </div>
  );
  if (!data) return <div className="p-6 flex gap-2 items-center text-sm text-slate-500" data-testid="bulk-reassign-assigned-loading"><Loader2 className="w-4 h-4 animate-spin" />Cargando personas asignadas…</div>;

  const selectedIds = new Set(selected.map(user => user.id));
  const query = search.trim().toLocaleLowerCase();
  const filtered = data.users.filter(user => `${user.name} ${user.email} ${user.id}`.toLocaleLowerCase().includes(query));
  const toggle = user => onChange(selectedIds.has(user.id) ? selected.filter(item => item.id !== user.id) : [...selected, user]);
  return (
    <div data-testid="bulk-reassign-assigned-list">
      <div className="p-4 space-y-2">
        <p className="text-xs text-slate-500" data-testid="bulk-reassign-assigned-count">{data.users.length} persona{data.users.length === 1 ? '' : 's'} asignada{data.users.length === 1 ? '' : 's'}</p>
        {data.not_found > 0 && <p className="text-xs text-amber-700" data-testid="bulk-reassign-assigned-missing">{data.not_found} activo{data.not_found === 1 ? '' : 's'} de la selección ya no existe{data.not_found === 1 ? '' : 'n'}.</p>}
        {data.users.length > 0 && <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} disabled={disabled}
            aria-label="Buscar personas asignadas" placeholder="Buscar persona asignada"
            className="input-base text-sm w-full" style={{ paddingLeft: '2.25rem' }} data-testid="bulk-reassign-assigned-search" />
        </div>}
      </div>
      {filtered.length === 0 && <p className="px-4 pb-5 text-sm text-slate-500" data-testid="bulk-reassign-assigned-empty">{data.users.length ? 'Sin coincidencias' : 'No hay personas asignadas a esta función.'}</p>}
      <div className="divide-y divide-slate-100">
        {filtered.map(user => (
          <label key={user.id} className={`flex gap-3 items-start px-4 py-3 cursor-pointer hover:bg-blue-50/40 ${selectedIds.has(user.id) ? 'bg-blue-50' : ''}`}>
            <input type="checkbox" checked={selectedIds.has(user.id)} onChange={() => toggle(user)} disabled={disabled}
              className="mt-1 shrink-0 accent-blue-600" data-testid={`bulk-reassign-remove-user-${user.id}`} />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800 [overflow-wrap:anywhere]" data-testid={`bulk-reassign-assigned-name-${user.id}`}>{user.name}</span>
              <span className="block text-xs text-slate-500 [overflow-wrap:anywhere]" data-testid={`bulk-reassign-assigned-email-${user.id}`}>{user.email || user.id}</span>
            </span>
            <span className="text-xs text-slate-500 shrink-0 pt-1" data-testid={`bulk-reassign-assigned-assets-${user.id}`}>{user.asset_count} activo{user.asset_count === 1 ? '' : 's'}</span>
          </label>
        ))}
      </div>
    </div>
  );
};